import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Alert, Accordion, Spinner, Table } from 'react-bootstrap';
import { CustomButton } from '../../components';
import { useAppSelector } from '../../hooks/redux';
import { selectIsSuperAdmin } from '../../features/currentUser/currentUserSelectors';
import { sendDuprResults } from '../../apis/dupr';
import { DuprMatchPlayerDto, DuprMatchResultDto, SendMissingMatchesResponseDto } from '../../generated/orval/schemas';
import { getDisplayName } from '../../utils/userDisplayName';
import { AdminStatistics } from './AdminStatistics';

const isProduction = window.location.hostname === 'admin.raqt.com';

const formatScores = (scores: string[]) => {
  return scores.join(', ');
};

const PlayerDetail = ({ player, t, prefix }: { player: DuprMatchPlayerDto; t: (key: string) => string; prefix: string }) => (
  <div className="d-flex justify-content-between ms-2 py-1" style={{ borderBottom: '1px solid #dee2e6' }}>
    <span>{getDisplayName(player)}</span>
    <span className={!player.duprId ? 'text-danger fw-bold' : 'text-muted'}>
      DUPR: {player.duprId || t(prefix + 'missingDuprId')}
    </span>
  </div>
);

const getTournamentLink = (result: DuprMatchResultDto) => {
  if (!result.organizationId) return null;
  return `/community/${result.organizationId}/tournament/${result.tournamentId}/tags/play`;
};

const ResultRow = ({ result, t, prefix }: { result: DuprMatchResultDto; t: (key: string) => string; prefix: string }) => {
  const [expanded, setExpanded] = useState(false);
  const link = getTournamentLink(result);
  const homePlayers = [result.homePlayer1, result.homePlayer2].filter(Boolean) as DuprMatchPlayerDto[];
  const awayPlayers = [result.awayPlayer1, result.awayPlayer2].filter(Boolean) as DuprMatchPlayerDto[];

  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        style={{
          cursor: 'pointer',
          backgroundColor: !result.success ? '#fff3f3' : undefined,
        }}
      >
        <td>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {result.matchDisplayId || `#${result.matchId}`}
            </a>
          ) : (
            <>{result.matchDisplayId || `#${result.matchId}`}</>
          )}
        </td>
        <td style={{ maxWidth: '200px' }}>
          {link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="d-inline-block text-truncate"
              style={{ maxWidth: '100%' }}
              title={result.tournamentName}
            >
              {result.tournamentName}
            </a>
          ) : (
            <span
              className="d-inline-block text-truncate"
              style={{ maxWidth: '100%' }}
              title={result.tournamentName}
            >
              {result.tournamentName}
            </span>
          )}
        </td>
        <td>
          {result.success ? (
            <span className="text-success">{t(prefix + 'success')}</span>
          ) : (
            <span className="text-danger fw-bold">{t(prefix + 'failed')}</span>
          )}
        </td>
        <td style={{ maxWidth: '300px' }}>
          {result.error ? (
            <span
              className="text-danger d-inline-block text-truncate"
              style={{ maxWidth: '100%' }}
              title={result.error}
            >
              {result.error}
            </span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </td>
        <td className="text-center">
          <span style={{ fontSize: '0.8em' }}>{expanded ? '▲' : '▼'}</span>
        </td>
      </tr>
      {expanded && (
        <tr style={{ backgroundColor: !result.success ? '#fff3f3' : '#f8f9fa' }}>
          <td colSpan={5}>
            <div className="p-2">
              <div className="row">
                <div className="col-md-6">
                  <strong>{t(prefix + 'matchId')}:</strong> #{result.matchId}
                  {result.matchDisplayId && (
                    <> ({result.matchDisplayId})</>
                  )}
                  <br />
                  <strong>{t(prefix + 'tournament')}:</strong> {result.tournamentName} (#{result.tournamentId})
                  <br />
                  <strong>{t(prefix + 'class')}:</strong> {result.className}
                  <br />
                  <strong>{t(prefix + 'matchDate')}:</strong>{' '}
                  {result.startDate
                    ? new Date(result.startDate).toLocaleString()
                    : '-'}
                  {result.organizationName && (
                    <>
                      <br />
                      <strong>{t(prefix + 'organization')}:</strong> {result.organizationName}
                    </>
                  )}
                </div>
                <div className="col-md-6">
                  <strong>{t(prefix + 'homePlayers')}:</strong>
                  {homePlayers.map((player) => (
                    <PlayerDetail key={player.id} player={player} t={t} prefix={prefix} />
                  ))}
                  <div className="mt-1">
                    <strong>{t(prefix + 'awayPlayers')}:</strong>
                    {awayPlayers.map((player) => (
                      <PlayerDetail key={player.id} player={player} t={t} prefix={prefix} />
                    ))}
                  </div>
                  <div className="mt-1">
                    <strong>{t(prefix + 'scores')}:</strong> {formatScores(result.scores)}
                  </div>
                </div>
              </div>
              {result.error && (
                <div className="mt-2">
                  <strong>{t(prefix + 'errorCol')}:</strong>{' '}
                  <span className="text-danger">{result.error}</span>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const AdminSendDupr = () => {
  const { t } = useTranslation();
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SendMissingMatchesResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statsOpened, setStatsOpened] = useState(false);

  const prefix = 'adminSendDupr-';

  if (!isSuperAdmin) return <Redirect to="/communities" />;

  const handleSend = async () => {
    if (!isProduction) {
      setError(t(prefix + 'devBlocked'));
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await sendDuprResults();
      setResults(data);
    } catch (e) {
      setError(t(prefix + 'error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-lg-5 px-sm-2 d-flex flex-grow-1 flex-column my-3 px-2 px-sm-0">
      <div className="row mt-5 mb-3">
        <div className="col d-flex align-items-center">
          <h3 className="font-weight-bold my-2 title">{t('adminTools-title')}</h3>
        </div>
      </div>

      <Accordion defaultActiveKey="0" onSelect={(key) => { if (key === '1') setStatsOpened(true); }}>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#000' }}>
              {t(prefix + 'title')}
            </span>
          </Accordion.Header>
          <Accordion.Body>
              <div className="mb-3">
                <CustomButton
                  className="d-flex justify-content-around align-items-center"
                  btnText={loading ? t(prefix + 'sending') : t(prefix + 'sendButton')}
                  variant="outline-dark"
                  disabled={loading}
                  cb={handleSend}
                >
                  {loading && <Spinner animation="border" size="sm" className="me-2" />}
                </CustomButton>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {results && (
                <>
                  <div className="mb-3">
                    <strong>{t(prefix + 'remainingMatches')}:</strong> {results.remainingMatchesToSubmit}
                    {' | '}
                    <strong>{t(prefix + 'totalSent')}:</strong> {results.sent}
                    {' | '}
                    <strong>{t(prefix + 'succeeded')}:</strong>{' '}
                    <span className="text-success">{results.succeeded}</span>
                    {' | '}
                    <strong>{t(prefix + 'totalFailed')}:</strong>{' '}
                    <span className={results.failed > 0 ? 'text-danger' : ''}>{results.failed}</span>
                  </div>

                  {results.results.length === 0 ? (
                    <Alert variant="info">{t(prefix + 'noResults')}</Alert>
                  ) : (
                    <Table bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>{t(prefix + 'matchId')}</th>
                          <th>{t(prefix + 'tournament')}</th>
                          <th>{t(prefix + 'status')}</th>
                          <th>{t(prefix + 'errorCol')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.results.map((result) => (
                          <ResultRow key={result.matchId} result={result} t={t} prefix={prefix} />
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#000' }}>
              {t('adminStats-title')}
            </span>
          </Accordion.Header>
          <Accordion.Body>
            {statsOpened && <AdminStatistics />}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};
